import type { RouterHistory } from '@tanstack/react-router'

import { postOpen } from '@/lib/embed/bridge'

/**
 * Keeps the member on the chart inside the app's WebView. A WebView has no
 * tabs, and a page of ours outside the chart would show the web app's own
 * chrome inside the app: everything that would leave is handed to the app as
 * an `open` (contract v1) instead, to open as its own screen.
 *
 * Three ways out, each caught once rather than at every link:
 *   - a click on an anchor — `NEW_TAB` links, plain hrefs, and router `Link`s,
 *     whose own handler skips a click already `defaultPrevented`;
 *   - a router navigation, through a history blocker (an alert card's
 *     `navigate`, a loader's redirect);
 *   - `window.open` (a toast's "See plans").
 *
 * Another origin is not the app's to open (contract rule 2: it blocks other
 * hosts), so those are only stopped.
 */
export const keepOnChart = ({
  history,
  staysOnChart,
}: {
  history: RouterHistory
  /** Whether an app path renders inside the chart's own layout. */
  staysOnChart: (pathname: string) => boolean
}): void => {
  /** True when the URL was handed to the app or stopped; false when it may go ahead. */
  const intercept = (url: URL, newTab: boolean): boolean => {
    if (url.origin !== window.location.origin) return true
    if (!newTab && staysOnChart(url.pathname)) return false
    postOpen(`${url.pathname}${url.search}${url.hash}`)
    return true
  }

  // Capture, so this runs before React's root listener and the router's Link.
  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented || event.button !== 0) return
      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null
      if (!(anchor instanceof HTMLAnchorElement)) return
      const newTab =
        (anchor.target !== '' && anchor.target !== '_self') ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey
      if (intercept(new URL(anchor.href), newTab)) event.preventDefault()
    },
    true,
  )

  history.block({
    blockerFn: ({ nextLocation, action }) => {
      // Back or forward out of the chart has no link to hand over; there is
      // nothing behind the chart in the WebView's history to reach anyway.
      if (action !== 'PUSH' && action !== 'REPLACE') return !staysOnChart(nextLocation.pathname)
      return intercept(new URL(nextLocation.href, window.location.origin), false)
    },
    // A blocker asks the browser to confirm every unload by default; a WebView
    // reload must not.
    enableBeforeUnload: false,
  })

  window.open = (url?: string | URL) => {
    if (url !== undefined && url !== '') intercept(new URL(url, window.location.href), true)
    return null
  }
}
